import React from "react";
// Shared with editProduct.jsx (Seller Edit Product) -- same DOM/CSS classes,
// single implementation of "select which values are active for this filter".
import FilterValuePicker from "../../pageInside/filters/FilterValuePicker";
import { elakayAutoText } from "../../pageInside/dataElakay";

// Filtres du Provider Product = UNION des filtres des catégories liées (dédupliquée
// par id de filtre, fournie par useLinkedCategories). Pour chaque filtre le Provider
// choisit les VALEURS autorisées (ensemble SOURCE : un Seller ne pourra vendre qu'un
// sous-ensemble), définit des combinaisons (variantes + quantité source) et les
// Display Settings (filtres affichés dans la Product View). Rien n'est codé en dur :
// tout vient des catégories/filtres réels.
const toDefs = (filterList, infoSource, filterIdOf) =>
  filterList.map((k) => {
    const translated = infoSource?.getFilTranslate ? infoSource.getFilTranslate(k) : { name: k._id?.name, FilterItem: [] };
    const key = String(k._id?.name || translated.name || "").toLowerCase().replace(/\s+/g, "").replace(/[^a-z0-9_]/g, "");
    return {
      filterId: filterIdOf(k),
      key,
      name: translated.name || k._id?.name,
      values: (translated.FilterItem || []).map((c) => ({ value: String(c[0]).trim(), label: c[1] })),
      // Real subFilter cascade config (FiltersEL.subFilter) on the raw
      // filter doc, if present -- additive, only read by Seller's
      // editProduct.jsx (cascade relocation into the linked-categories
      // checklist). Provider never reads this field.
      subFilter: k._id?.subFilter || [],
    };
  });

const ProviderFiltersEditor = ({ filterList, filterIdOf, infoSource, sourceFilters, setSourceFilters, variants, setVariants, reservedUnits = null, displayFilters, setDisplayFilters, t }) => {
  const defs = toDefs(filterList, infoSource, filterIdOf);
  if (defs.length === 0) return <p className="text-muted">{t("t173")}</p>;

  const chosen = (def) => sourceFilters[def.filterId] || [];
  const toggle = (def, value) =>
    setSourceFilters((prev) => {
      const cur = prev[def.filterId] || [];
      return { ...prev, [def.filterId]: cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value] };
    });
  const toggleAll = (def) =>
    setSourceFilters((prev) => ({
      ...prev,
      [def.filterId]: chosen(def).length === def.values.length ? [] : def.values.map((v) => v.value),
    }));
  const activeDefs = defs.filter((d) => chosen(d).length > 0);

  const setVariant = (index, patch) => setVariants((prev) => prev.map((v, i) => (i === index ? { ...v, ...patch } : v)));
  const setVariantValue = (index, def, value) =>
    setVariants((prev) =>
      prev.map((v, i) => {
        if (i !== index) return v;
        const filter = { ...(v.filter || {}) };
        if (value) filter[def.key] = value;
        else delete filter[def.key];
        return { ...v, filter };
      })
    );

  return (
    <div className="provider-filters">
      <h3 className="h6">{t("t166")}</h3>
      <small className="text-muted d-block mb-2">{t("t170")}</small>
      <FilterValuePicker defs={defs} selected={sourceFilters} onToggleValue={toggle} onToggleAll={toggleAll} selectAllLabel={t("t167")} />

      {activeDefs.length > 0 && (
        <>
          {/* Variants & Quantity: one row per combination with its own available quantity (the
              product total is their sum -- see providerProductService.syncStockWithVariants). */}
          <h3 className="h6 mt-3">{t("t168")} · {elakayAutoText("h_pqty_variants_quantity", "Variants & Quantity")}</h3>
          <small className="text-muted d-block mb-2">{elakayAutoText("h_pqty_variants_hint", "Set the available quantity of each combination. A combination at 0 cannot be sold.")}</small>
          {variants.length > 0 && (
            <div className="provider-variants" role="table" aria-label={elakayAutoText("h_pqty_variants_quantity", "Variants & Quantity")}>
              <div className="provider-variants__row provider-variants__row--head" role="row">
                {activeDefs.map((def) => <span key={def.filterId} role="columnheader">{def.name}</span>)}
                <span role="columnheader">{elakayAutoText("h_pqty_available", "Available Quantity")}</span>
                <span role="columnheader">{elakayAutoText("h_pqty_status", "Status")}</span>
                <span role="columnheader"><span className="sr-only">{t("t174")}</span></span>
              </div>
              {variants.map((variant, index) => {
                const qty = Math.max(0, Math.floor(Number(variant.quantity) || 0));
                return (
                  <div key={index} className="provider-variants__row" role="row" data-testid="provider-variant-row">
                    {activeDefs.map((def) => (
                      <label key={def.filterId} className="provider-variants__cell" role="cell">
                        <span className="provider-variants__label">{def.name}</span>
                        <select aria-label={`${def.name} #${index + 1}`} className="form-control" value={variant.filter?.[def.key] || ""} onChange={(e) => setVariantValue(index, def, e.target.value)}>
                          <option value="">{def.name}</option>
                          {def.values.filter((v) => chosen(def).includes(v.value)).map((v) => (
                            <option key={v.value} value={v.value}>{v.label}</option>
                          ))}
                        </select>
                      </label>
                    ))}
                    <label className="provider-variants__cell" role="cell">
                      <span className="provider-variants__label">{elakayAutoText("h_pqty_available", "Available Quantity")}</span>
                      <input type="number" min="0" step="1" inputMode="numeric" className="form-control" aria-label={`${t("t052")} #${index + 1}`} value={variant.quantity} onChange={(e) => setVariant(index, { quantity: e.target.value })} />
                    </label>
                    <span className="provider-variants__cell" role="cell">
                      <span className={`provider-variants__badge provider-variants__badge--${qty > 0 ? "in" : "out"}`}>
                        {qty > 0 ? elakayAutoText("h_pqty_in_stock", "Available") : elakayAutoText("h_pqty_out_of_stock", "Out of Stock")}
                      </span>
                    </span>
                    <span className="provider-variants__cell" role="cell">
                      <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => setVariants((prev) => prev.filter((_, i) => i !== index))}>
                        {t("t174")}
                      </button>
                    </span>
                  </div>
                );
              })}
              <div className="provider-variants__total" role="row" data-testid="provider-variants-total">
                <span>{elakayAutoText("h_pqty_total_available", "Total available quantity")}</span>
                <strong>{variants.filter((v) => Object.keys(v.filter || {}).length).reduce((n, v) => n + Math.max(0, Math.floor(Number(v.quantity) || 0)), 0).toLocaleString()}</strong>
                {reservedUnits > 0 && <small>{elakayAutoText("h_pqty_reserved", "Reserved by open orders")}: {Number(reservedUnits).toLocaleString()}</small>}
              </div>
            </div>
          )}
          <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => setVariants((prev) => [...prev, { filter: {}, quantity: 0 }])}>
            {t("t169")}
          </button>

          <h3 className="h6 mt-3">{t("t171")}</h3>
          <small className="text-muted d-block mb-1">{t("t172")}</small>
          <div className="provider-filters__values">
            {activeDefs.map((def) => (
              <label key={def.key} className="provider-filters__value">
                <input type="checkbox" checked={displayFilters.includes(def.key)} onChange={() => setDisplayFilters((prev) => (prev.includes(def.key) ? prev.filter((k) => k !== def.key) : [...prev, def.key]))} /> {def.name}
              </label>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export { toDefs };
export default ProviderFiltersEditor;
